<template>
  <div class="sensor-normalizations-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1>Sensor Normalizations</h1>
          <p>Gestión de normalizaciones de sensores</p>
        </v-col>
      </v-row>
      
      <!-- Filtros -->
      <v-row>
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedSensorId"
            :items="sensorOptions"
            label="Filtrar por Sensor"
            clearable
            @update:model-value="filterBySensor"
          ></v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedSensorSettingId"
            :items="sensorSettingOptions"
            label="Filtrar por Configuración"
            clearable
            @update:model-value="filterBySensorSetting"
          ></v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Filtrar por Estado"
            clearable
            @update:model-value="filterByStatus"
          ></v-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="sensorNormalizationsList"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Lista de Normalizaciones de Sensores</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="refreshData">
                  <v-icon left>mdi-refresh</v-icon>
                  Actualizar
                </v-btn>
                <v-btn color="success" class="ml-2" @click="addItem">
                  <v-icon left>mdi-plus</v-icon>
                  Agregar Normalización
                </v-btn>
              </v-toolbar>
            </template>
            <template v-slot:item.sensorId="{ item }">
              <v-chip color="blue" variant="outlined" small>
                {{ item.sensorId }}
              </v-chip>
            </template>
            <template v-slot:item.sensorSettingId="{ item }">
              <v-chip color="green" variant="outlined" small>
                {{ item.sensorSettingId }}
              </v-chip>
            </template>
            <template v-slot:item.initDate="{ item }">
              {{ formatDate(item.initDate) }}
            </template>
            <template v-slot:item.endDate="{ item }">
              {{ formatDate(item.endDate) }}
            </template>
            <template v-slot:item.multDelta="{ item }">
              <v-chip color="purple" small>
                ×{{ item.multDelta }}
              </v-chip>
            </template>
            <template v-slot:item.sumDelta="{ item }">
              <v-chip color="orange" small>
                +{{ item.sumDelta }}
              </v-chip>
            </template>
            <template v-slot:item.divDelta="{ item }">
              <v-chip color="teal" small>
                ÷{{ item.divDelta }}
              </v-chip>
            </template>
            <template v-slot:item.refreshed="{ item }">
              <v-chip :color="item.refreshed ? 'success' : 'warning'" small>
                {{ item.refreshed ? 'Actualizado' : 'Pendiente' }}
              </v-chip>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item)" small>
                {{ getStatusText(item) }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn 
                size="small" 
                icon 
                variant="text" 
                @click="refreshNormalization(item)"
                :disabled="item.refreshed"
              >
                <v-icon>mdi-refresh</v-icon>
                <v-tooltip activator="parent" location="top">
                  Actualizar normalización
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="editItem(item)">
                <v-icon>mdi-pencil</v-icon>
                <v-tooltip activator="parent" location="top">
                  Editar normalización
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="deleteItem(item)">
                <v-icon>mdi-delete</v-icon>
                <v-tooltip activator="parent" location="top">
                  Eliminar normalización
                </v-tooltip>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useSensorNormalizationStore } from './store'
import { SensorNormalization } from './models/sensorNormalization'

const sensorNormalizationStore = useSensorNormalizationStore()
const loading = ref(false)
const selectedSensorId = ref<string | null>(null)
const selectedSensorSettingId = ref<string | null>(null)
const statusFilter = ref<string | null>(null)

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Sensor ID', key: 'sensorId' },
  { title: 'Config ID', key: 'sensorSettingId' },
  { title: 'Inicio', key: 'initDate' },
  { title: 'Fin', key: 'endDate' },
  { title: 'Mult', key: 'multDelta' },
  { title: 'Suma', key: 'sumDelta' },
  { title: 'Div', key: 'divDelta' },
  { title: 'Actualizado', key: 'refreshed' },
  { title: 'Estado', key: 'status' },
  { title: 'Acciones', key: 'actions', sortable: false },
]

// Opciones para los filtros (en un caso real, estos vendrían de otros stores)
const sensorOptions = ref([
  { title: 'Sensor A', value: 'sensorA' },
  { title: 'Sensor B', value: 'sensorB' },
  { title: 'Sensor C', value: 'sensorC' },
])

const sensorSettingOptions = ref([
  { title: 'Config 1', value: 'config1' },
  { title: 'Config 2', value: 'config2' },
  { title: 'Config 3', value: 'config3' },
])

const statusOptions = ref([
  { title: 'Activo', value: 'active' },
  { title: 'Expirado', value: 'expired' },
  { title: 'Futuro', value: 'future' },
  { title: 'Actualizado', value: 'refreshed' },
  { title: 'Pendiente', value: 'pending' },
])

const sensorNormalizationsList = computed(() => {
  let list = sensorNormalizationStore.sensorNormalizations
  
  if (selectedSensorId.value) {
    list = list.filter(item => item.sensorId === selectedSensorId.value)
  }
  
  if (selectedSensorSettingId.value) {
    list = list.filter(item => item.sensorSettingId === selectedSensorSettingId.value)
  }
  
  if (statusFilter.value) {
    const now = new Date()
    list = list.filter(item => {
      const initDate = new Date(item.initDate)
      const endDate = new Date(item.endDate)
      
      switch (statusFilter.value) {
        case 'active':
          return now >= initDate && now <= endDate
        case 'expired':
          return now > endDate
        case 'future':
          return now < initDate
        case 'refreshed':
          return item.refreshed
        case 'pending':
          return !item.refreshed
        default:
          return true
      }
    })
  }
  
  return list
})

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('es-ES')
}

const getStatusColor = (item: SensorNormalization) => {
  const now = new Date()
  const initDate = new Date(item.initDate)
  const endDate = new Date(item.endDate)
  
  if (now < initDate) return 'info'     // Futuro
  if (now > endDate) return 'error'     // Expirado
  if (now >= initDate && now <= endDate) return 'success' // Activo
  return 'default'
}

const getStatusText = (item: SensorNormalization) => {
  const now = new Date()
  const initDate = new Date(item.initDate)
  const endDate = new Date(item.endDate)
  
  if (now < initDate) return 'Futuro'
  if (now > endDate) return 'Expirado'
  if (now >= initDate && now <= endDate) return 'Activo'
  return 'Indefinido'
}

const refreshData = async () => {
  loading.value = true
  try {
    await sensorNormalizationStore.getSensorNormalizations()
  } finally {
    loading.value = false
  }
}

const filterBySensor = async (sensorId: string | null) => {
  if (sensorId) {
    loading.value = true
    try {
      await sensorNormalizationStore.getSensorNormalizationsBySensorId(sensorId)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterBySensorSetting = async (sensorSettingId: string | null) => {
  if (sensorSettingId) {
    loading.value = true
    try {
      await sensorNormalizationStore.getSensorNormalizationsBySensorSettingId(sensorSettingId)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByStatus = () => {
  // El filtrado por estado se maneja en el computed
}

const addItem = () => {
  // Implementar lógica para agregar normalización
  console.log('Add new sensor normalization')
}

const editItem = (item: SensorNormalization) => {
  // Implementar lógica de edición
  console.log('Edit item:', item)
}

const deleteItem = (item: SensorNormalization) => {
  // Implementar lógica de eliminación
  console.log('Delete item:', item)
}

const refreshNormalization = async (item: SensorNormalization) => {
  try {
    loading.value = true
    await sensorNormalizationStore.refreshNormalization(item.id)
    await refreshData()
  } catch (error) {
    console.error('Error refreshing normalization:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.sensor-normalizations-view {
  height: 100%;
}
</style>
